import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { MapPin, Phone, Mail, Building2, Stethoscope, ArrowLeft, Calendar, MessageSquare, Shield } from "lucide-react";
import Logo from "@/components/app/Logo";
import { Button } from "@/components/ui/button";

const PROFESSION_ICON = {
  Médecin: "🩺", Pharmacien: "💊", "Chirurgien-dentiste": "🦷", "Sage-femme": "👶", Infirmier: "💉",
  "Masseur-kinésithérapeute": "💆", "Pédicure-podologue": "🦶", Orthophoniste: "🗣️", Ostéopathe: "🌿",
  Psychologue: "🧠", Diététicien: "🥗", "Manipulateur d'électroradiologie": "📡", Ambulancier: "🚑",
  "Opticien-lunetier": "👓", Audioprothésiste: "🎧",
};

function seoSetTitle(title, description) {
  if (typeof document === "undefined") return;
  document.title = title;
  let meta = document.querySelector('meta[name="description"]');
  if (!meta) { meta = document.createElement("meta"); meta.name = "description"; document.head.appendChild(meta); }
  meta.content = description;
  // Open Graph
  const setOg = (prop, val) => {
    let og = document.querySelector(`meta[property="${prop}"]`);
    if (!og) { og = document.createElement("meta"); og.setAttribute("property", prop); document.head.appendChild(og); }
    og.content = val;
  };
  setOg("og:title", title);
  setOg("og:description", description);
  setOg("og:type", "profile");
}

export default function PublicPro() {
  const { rpps } = useParams();
  const [pro, setPro] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/directory/pros/${rpps}`, { _skipAuth: true })
      .then(({ data }) => {
        setPro(data);
        const desc = [data.specialty, data.city, "sur Doctobook - Réseau social de la santé"].filter(Boolean).join(" · ");
        seoSetTitle(`${data.display_name} · ${data.specialty || data.profession} · ${data.city || "France"} — Doctobook`, desc);
        // JSON-LD Person schema for SEO
        const jsonLd = {
          "@context": "https://schema.org",
          "@type": "Physician",
          name: data.display_name,
          medicalSpecialty: data.specialty,
          telephone: data.phone,
          email: data.email,
          address: {
            "@type": "PostalAddress",
            streetAddress: data.address,
            postalCode: data.zip_code,
            addressLocality: data.city,
            addressCountry: "FR",
          },
          identifier: [{ "@type": "PropertyValue", propertyID: "RPPS", value: data.rpps }],
        };
        let script = document.getElementById("pro-jsonld");
        if (!script) {
          script = document.createElement("script");
          script.id = "pro-jsonld";
          script.type = "application/ld+json";
          document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(jsonLd);
      })
      .catch(() => setNotFound(true));

    return () => {
      // cleanup jsonld
      const script = document.getElementById("pro-jsonld");
      if (script) script.remove();
    };
  }, [rpps]);

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-10 max-w-md text-center">
          <Stethoscope className="h-12 w-12 mx-auto text-slate-300 mb-3" />
          <h1 className="font-display text-2xl font-bold text-deep">Professionnel introuvable</h1>
          <p className="text-slate-500 mt-2 mb-4">Le numéro RPPS <code>{rpps}</code> n'est pas référencé dans notre annuaire.</p>
          <Link to="/dashboard/directory"><Button className="btn-primary">Retour à l'annuaire</Button></Link>
        </div>
      </div>
    );
  }

  if (!pro) return <div className="min-h-screen flex items-center justify-center text-slate-400">Chargement…</div>;

  const icon = PROFESSION_ICON[pro.profession] || "🩺";
  const fullAddress = [pro.address, pro.zip_code, pro.city].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-100 bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <div className="flex gap-2">
            <Link to="/dashboard/directory"><Button variant="ghost" size="sm"><ArrowLeft className="h-4 w-4 me-1" />Annuaire</Button></Link>
            <Link to="/register"><Button size="sm" className="btn-primary">Rejoindre Doctobook</Button></Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Hero card */}
        <article className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" itemScope itemType="https://schema.org/Physician">
          <div className="bg-gradient-to-br from-[#618DB8] to-[#2A4365] p-8 text-white">
            <div className="flex items-center gap-5">
              <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-3xl font-extrabold border-2 border-white/30">
                {pro.first_name?.charAt(0) || "?"}{pro.last_name?.charAt(0) || ""}
              </div>
              <div>
                <h1 className="font-display text-3xl font-extrabold" itemProp="name">{pro.display_name}</h1>
                <div className="mt-1 flex items-center gap-2 flex-wrap">
                  <span className="inline-flex items-center gap-1 bg-white/15 backdrop-blur rounded-full px-2.5 py-1 text-xs font-semibold">
                    {icon} <span itemProp="medicalSpecialty">{pro.specialty || pro.profession}</span>
                  </span>
                  <span className="inline-flex items-center gap-1 bg-emerald-500/25 backdrop-blur rounded-full px-2.5 py-1 text-xs font-semibold">
                    <Shield className="h-3 w-3" />Vérifié RPPS
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-8 grid gap-5 sm:grid-cols-2">
            {fullAddress && (
              <div className="flex items-start gap-3">
                <MapPin className="h-5 w-5 text-[#618DB8] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">Adresse</div>
                  <div className="text-sm text-slate-800 mt-0.5" itemProp="address">{fullAddress}</div>
                </div>
              </div>
            )}
            {pro.phone && (
              <div className="flex items-start gap-3">
                <Phone className="h-5 w-5 text-[#618DB8] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">Téléphone</div>
                  <a href={`tel:${pro.phone}`} className="text-sm text-slate-800 hover:text-[#618DB8]" itemProp="telephone">{pro.phone}</a>
                </div>
              </div>
            )}
            {pro.email && (
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-[#618DB8] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">Email</div>
                  <a href={`mailto:${pro.email}`} className="text-sm text-slate-800 hover:text-[#618DB8]" itemProp="email">{pro.email}</a>
                </div>
              </div>
            )}
            {pro.facility && (
              <div className="flex items-start gap-3">
                <Building2 className="h-5 w-5 text-[#618DB8] flex-shrink-0 mt-0.5" />
                <div>
                  <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">Établissement</div>
                  <div className="text-sm text-slate-800 mt-0.5">{pro.facility}</div>
                </div>
              </div>
            )}
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-xs uppercase font-bold text-slate-500 tracking-wide">RPPS</div>
                <div className="text-sm text-slate-800 mt-0.5 font-mono">{pro.rpps}</div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-100 bg-slate-50 p-8">
            <h2 className="font-display text-lg font-bold text-deep mb-3">Contacter {pro.civility || ""} {pro.last_name} sur Doctobook</h2>
            <p className="text-sm text-slate-600 mb-4">
              {pro.display_name} est référencé dans notre annuaire national. Créez votre compte gratuit pour prendre rendez-vous en ligne, envoyer un message sécurisé ou consulter en téléconsultation.
            </p>
            <div className="flex gap-2 flex-wrap">
              <Link to="/register?role=patient"><Button className="btn-coral font-semibold"><Calendar className="h-4 w-4 me-1.5" />Prendre rendez-vous</Button></Link>
              <Link to="/register?role=patient"><Button variant="outline" className="border-[#618DB8] text-[#618DB8]"><MessageSquare className="h-4 w-4 me-1.5" />Message sécurisé</Button></Link>
            </div>
          </div>
        </article>

        {/* SEO content block */}
        <section className="mt-10 prose prose-slate max-w-none">
          <h2 className="font-display text-2xl font-bold text-deep">À propos de {pro.display_name}</h2>
          <p className="text-slate-600 leading-relaxed">
            {pro.display_name} exerce en tant que <strong>{pro.specialty || pro.profession}</strong>{pro.city ? ` à ${pro.city}` : ""}
            {pro.zip_code ? ` (${pro.zip_code})` : ""}. Ce profil professionnel de santé est référencé dans l'annuaire national officiel
            (RPPS · Répertoire Partagé des Professionnels de Santé) tenu par l'Agence du Numérique en Santé (ANS).
          </p>
          <p className="text-slate-600 leading-relaxed">
            Sur <strong>Doctobook — Le réseau social de la santé</strong>, vous pouvez :
          </p>
          <ul className="text-slate-600">
            <li>Consulter sa fiche professionnelle vérifiée RPPS</li>
            <li>Prendre rendez-vous en ligne (sous réserve que le praticien ait activé cette option)</li>
            <li>Échanger via messagerie sécurisée chiffrée</li>
            <li>Bénéficier d'une téléconsultation en cas de disponibilité</li>
          </ul>
        </section>

        <footer className="border-t border-slate-100 mt-16 pt-8 text-xs text-slate-400 flex flex-wrap gap-4 justify-between">
          <div>© Doctobook · Annuaire national · Source RPPS-ANS / data.gouv (Licence Etalab)</div>
          <div className="flex gap-4">
            <Link to="/legal/mentions-legales" className="hover:text-slate-700">Mentions légales</Link>
            <Link to="/legal/privacy" className="hover:text-slate-700">Confidentialité</Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
